import { useState, useRef } from 'react';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { FileUpload, Loading } from '@/components/shared';
import { FileUploadHandles } from '@/components/shared/FileUpload';
import { SUPPORTED_UPLOAD_FILE_TYPES_STRING } from '@/constants/supported-file-types';
import { ConnectSourcesControls } from '@/components/shared/OutlookSyncProgress';
import { DOCUMENT_ENRICHMENT_SECTIONS, useDocuments } from '@/hooks/useDocuments';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { useFolderTree } from '@/hooks/useFolders';
import { CreateFolderDialog } from '@/components/folders';
import { FolderTree } from '@/components/folders/FolderTree';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { filterFilesWithToast } from '@/lib/fileFilterUtils';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useRegionBundlePaths } from '@/lib/bundleRoutes';
import { toolbarActionLabelClass, toolbarContainerClass } from '@/lib/toolbar-compact';
import { cn } from '@/lib/utils';
import {
  Layers,
  FolderPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/NewButton';

const CaseFilesDirectory = () => {
  const { selectedCase } = useCaseContext();
  const { slug } = useParams({ from: '/matter/$slug/case-files' });
  const search = useSearch({ from: '/matter/$slug/case-files' });
  const bundlePaths = useRegionBundlePaths();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const timezone = getTimezone();
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const fileUploadRef = useRef<FileUploadHandles>(null);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(slug as string | undefined);
  const deepAnalysisOnly = deepAnalysisEnabled && search.view === 'deep-analysis';
  useShellHeader({
    header: 'Case Files',
    subheader: 'Browse and manage your case documents',
    hideBottomBorder: true,
  });

  // Use the custom hook for document data, passing the timezone
  const { documents, error, mutate, isLoading: documentsLoading } = useDocuments(
    slug as string,
    undefined, // documentTypes
    undefined, // isDraft
    undefined, // folderIds - show all documents on main page
    {},
    timezone,
    // Only the count is used, and this matches Case Partner's key so the two share one fetch.
    { analysisSetOnly: deepAnalysisOnly, excludeSections: DOCUMENT_ENRICHMENT_SECTIONS }
  );

  // Use the folder tree hook
  const { folderTree, isLoading: foldersLoading, mutate: mutateFolders } = useFolderTree(
    slug as string,
    { analysisSetOnly: deepAnalysisOnly }
  );

  // Documents only feed the "All Files" count, so don't hold the folder tree back for them.
  const isLoading = foldersLoading;

  // Handle file upload completion
  const handleUploadComplete = () => {
    setTargetFolderId(null); // Reset target folder
    mutate(); // Refresh documents data
    mutateFolders(); // Refresh folder tree
  };

  // Handle folder operations
  const handleFolderRefresh = async () => {
    try {
      await mutateFolders();
      await mutate();
    } catch (error) {
      console.error('Error refreshing folder data:', error);
    }
  };

  // Handle files dropped on a folder
  const handleFilesDroppedOnFolder = (files: File[], folderId: number) => {
    // Filter files using shared utility with toast notifications
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    // Set the target folder ID so FileUpload can use it
    const folderIdString = folderId.toString();
    setTargetFolderId(folderIdString);
    // Open upload dialog with filtered files, passing folderId directly to ensure correct selection
    fileUploadRef.current?.openWithFiles(validFiles, folderIdString);
  };

  // Flatten folder tree for sorting
  const flattenFolderTree = (folders: any[], depth: number = 0): any[] => {
    const flattened: any[] = [];

    for (const folder of folders) {
      flattened.push({
        ...folder,
        depth,
        count: folder.document_count || 0,
        isFolder: true
      });

      if (folder.children && folder.children.length > 0) {
        flattened.push(...flattenFolderTree(folder.children, depth + 1));
      }
    }

    return flattened;
  };

  // Handle sorting - preserve hierarchical order by sorting the tree before flattening
  const sortFolderTree = (folders: any[]): any[] => {
    const sorted = [...folders].sort((a, b) => a.name.localeCompare(b.name));

    // Recursively sort children
    return sorted.map(folder => ({
      ...folder,
      children: folder.children ? sortFolderTree(folder.children) : []
    }));
  };

  const sortedFolderTree = folderTree && folderTree.length > 0
    ? sortFolderTree(folderTree)
    : [];

  const sortedFolders = flattenFolderTree(sortedFolderTree);

  return (
    <div className="overflow-x-hidden">
      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : (
        <div className="px-6 py-6">
          <div className={cn('flex flex-wrap items-center gap-2 mb-4', toolbarContainerClass)}>
            <div className="flex flex-wrap justify-end gap-2 items-center ml-auto">
              <Link to={bundlePaths.index} params={{ slug: slug as string }}>
                <Button
                  variant="outline"
                  size="sm"
                  icon={Layers}
                  text={ui_labels.bundle_plural}
                  textClassName={toolbarActionLabelClass}
                  tooltip={ui_labels.bundle_plural}
                  aria-label={ui_labels.bundle_plural}
                />
              </Link>
              <ConnectSourcesControls matterId={slug as string} />

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateFolderDialogOpen(true)}
                icon={FolderPlus}
                text="Create Folder"
                textClassName={toolbarActionLabelClass}
                tooltip="Create Folder"
                aria-label="Create Folder"
              />

              <FileUpload
                ref={fileUploadRef}
                matterGuid={slug as string}
                buttonText="Upload Files"
                onUploadComplete={handleUploadComplete}
                showAsDialog={true}
                acceptTypes={SUPPORTED_UPLOAD_FILE_TYPES_STRING}
                documentType={targetFolderId}
              />
            </div>
          </div>

          {error ? (
            <div className="p-4 border border-red-200 rounded-md bg-red-50 text-red-600">
              Error loading document data. Please try again later.
            </div>
          ) : isLoading ? (
            <div className="flex flex-col gap-2 justify-center items-center my-12">
              <Loading />
              <span className="text-sm font-medium text-gray-700">Loading document folders...</span>
            </div>
          ) : (
            <>
              <FolderTree
                folders={sortedFolders}
                totalDocuments={documentsLoading ? null : documents.length}
                matterSlug={slug as string}
                onRefresh={handleFolderRefresh}
                onFilesDropped={handleFilesDroppedOnFolder}
                showDeepAnalysis={deepAnalysisEnabled}
                deepAnalysisOnly={deepAnalysisOnly}
                rowClassName="py-1.5"
              />
            </>
          )}
        </div>
      )}

      <CreateFolderDialog
        matterSlug={slug as string}
        isOpen={createFolderDialogOpen}
        onClose={() => setCreateFolderDialogOpen(false)}
        onSuccess={handleFolderRefresh}
      />
    </div>
  );
};
export default CaseFilesDirectory;
